import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { Box, Grid } from '@mui/material';
import { FOR_BANK_SECTIONS, FOR_BANK_SUB_SECTIONS } from 'constants/businessToolSections/forBank';
import { DATE_FORMAT_MM_DOT_DD_DOT_YYYY } from 'constants/formats';
import dayjs from 'dayjs';
import { useUpdateBusinessPlan } from 'services/businessPlan/hooks';
import { useBusinessToolStore } from 'store/useBusinessToolStore';
import { getBusinessPlan, getSetBusinessToolTitle } from 'store/useBusinessToolStore/selectors';
import { useUserStore } from 'store/useUserStore';
import { getAuthUser } from 'store/useUserStore/selectors';

import useCheckSubSectionAccess from 'hooks/useCheckSubSectionAccess';
import { useCreateFirstProduct } from 'hooks/useCreateFirstProduct';

import { CurrencyAutocomplete } from 'components/_basic/CurrencyAutocomplete';
import { DatePickerControl } from 'components/_basic/Form/DatePicker';
import { NumberInputControlled } from 'components/_basic/Form/NumberInput';
import { TextInputControl } from 'components/_basic/Form/TextInput';
import { FormHeader } from 'components/_basic/FormHeader/FormHeader';
import { FormSectionCard } from 'components/_basic/FormSectionCard/FormSectionCard';
import { FORM_SECTION_CARD_VARIANT } from 'components/_basic/FormSectionCard/types';
import { TooltipFormImageUpload } from 'components/BusinessToolForm/components/TooltipFormImageUpload/TooltipFormImageUpload';
import { useHandleRequiredForAIInputBlur } from 'components/BusinessToolForm/hooks/useHandleRequiredForAIInputBlur';
import { LifeCycleStages } from 'components/BusinessToolForm/Shared/LifeCycleStages';

import { getValidatedMetadata } from 'helpers/metadataParser';
import { concatWithDot } from 'helpers/string';

import { COMPANY_OVERVIEW_NAMES, prefix } from './utils/form';
import { OperationIndustry } from '../../Global/components/OperationIndustry/OperationIndustry';
import { GLOBAL_FIELD_NAMES, globalPrefix } from '../../Global/form';

export const CompanyOverview = () => {
  const { t } = useTranslation();
  const { control, getValues } = useFormContext();

  const [companyNamePrevState, setCompanyNamePrevState] = useState(
    getValues(concatWithDot(globalPrefix, GLOBAL_FIELD_NAMES.COMPANY_NAME)),
  );
  const { id: businessPlanId = '' } = useParams();

  const authUser = useUserStore(getAuthUser);

  const setBusinessToolTitle = useBusinessToolStore(getSetBusinessToolTitle);

  const businessPlan = useBusinessToolStore(getBusinessPlan);

  const updateMutation = useUpdateBusinessPlan();

  const canAccess = useCheckSubSectionAccess(
    FOR_BANK_SECTIONS.OVERVIEW,
    FOR_BANK_SUB_SECTIONS.FOR_BANK_OVERVIEW.COMPANY,
  );

  useCreateFirstProduct();

  const handleRequiredForAIInputBlur = useHandleRequiredForAIInputBlur();

  const handleCompanyNameBlur = async () => {
    handleRequiredForAIInputBlur();
    const companyName = getValues(concatWithDot(globalPrefix, GLOBAL_FIELD_NAMES.COMPANY_NAME));
    const sameNames = companyNamePrevState === companyName;

    if (authUser && !sameNames) {
      try {
        const params = {
          id: businessPlanId,
          body: {
            title: companyName,
            metadata: JSON.stringify({
              ...getValidatedMetadata(businessPlan.metadata),
              isFirstTryToSetBusinessToolTitle: false,
            }),
          },
        };
        await updateMutation.mutateAsync(params);
        setBusinessToolTitle(companyName);
        setCompanyNamePrevState(companyName);
      } catch (error) {
        return;
      }
    }
  };

  return (
    <Box>
      <FormSectionCard variant={FORM_SECTION_CARD_VARIANT.LARGE}>
        <FormHeader
          section={FOR_BANK_SECTIONS.OVERVIEW}
          locked={!canAccess}
          title={t('overviewTitle')}
          badgeText={t('overview.company.title')}
        />
      </FormSectionCard>
      <FormSectionCard>
        <Grid container spacing={3}>
          <Grid item xs="auto">
            <TooltipFormImageUpload
              width={64}
              height={64}
              placeholder="Logo"
              name={concatWithDot(prefix, COMPANY_OVERVIEW_NAMES.FILE)}
              disabled={!canAccess}
            />
          </Grid>
          {/* 0.1.1 */}
          <Grid item xs>
            <TextInputControl
              control={control}
              disabled={!canAccess}
              id="overview.company.name"
              name={concatWithDot(globalPrefix, GLOBAL_FIELD_NAMES.COMPANY_NAME)}
              onBlur={handleCompanyNameBlur}
              label={t('overview.company.name')}
              maxLength={70}
              requiredForAI
            />
          </Grid>
        </Grid>
      </FormSectionCard>
      <FormSectionCard>
        <Grid container spacing={3} display="flex" alignItems="flex-end">
          {/* 0.1.2 */}
          <Grid item xs={6}>
            <DatePickerControl
              control={control}
              maxDate={dayjs().subtract(1, 'day')}
              disabled={!canAccess}
              id="overview.company.dateOfRegistration"
              name={concatWithDot(globalPrefix, GLOBAL_FIELD_NAMES.REGISTRATION_DATE)}
              label={t('overview.company.dateOfRegistration')}
              dateFormat={DATE_FORMAT_MM_DOT_DD_DOT_YYYY}
            />
          </Grid>
          {/* 0.1.3 */}
          <Grid item xs={6}>
            <TextInputControl
              control={control}
              disabled={!canAccess}
              id="overview.company.address"
              name={concatWithDot(globalPrefix, GLOBAL_FIELD_NAMES.ADDRESS)}
              label={t('overview.company.address')}
              maxLength={150}
              requiredForAI
              onBlur={handleRequiredForAIInputBlur}
            />
          </Grid>
        </Grid>
      </FormSectionCard>
      {/* 0.1.4 */}
      <FormSectionCard
        title={t('overview.company.briefCompanyDescription')}
        aiFieldName={concatWithDot(globalPrefix, GLOBAL_FIELD_NAMES.COMPANY_BRIEF_DESCRIPTION)}
        aiOnlyUpdate
        disabled={!canAccess}
        requiredForAI
      >
        <TextInputControl
          multiline
          control={control}
          disabled={!canAccess}
          id="overview.company.briefCompanyDescription"
          name={concatWithDot(globalPrefix, GLOBAL_FIELD_NAMES.COMPANY_BRIEF_DESCRIPTION)}
          minRows={4}
          maxLength={1000}
          onBlur={handleRequiredForAIInputBlur}
        />
      </FormSectionCard>
      {/* 0.1.5 */}
      <FormSectionCard
        title={t('overview.project.briefProjectDescription')}
        aiFieldName={concatWithDot(globalPrefix, GLOBAL_FIELD_NAMES.PROJECT_BRIEF_DESCRIPTION)}
        aiOnlyUpdate
        disabled={!canAccess}
        requiredForAI
      >
        <TextInputControl
          multiline
          control={control}
          id="overview.company.briefProjectDescription"
          name={concatWithDot(globalPrefix, GLOBAL_FIELD_NAMES.PROJECT_BRIEF_DESCRIPTION)}
          disabled={!canAccess}
          minRows={4}
          maxLength={1000}
          onBlur={handleRequiredForAIInputBlur}
        />
      </FormSectionCard>
      {/* 0.1.6 */}
      <FormSectionCard title={t('overview.company.lifeCycleStage')} disabled={!canAccess}>
        <LifeCycleStages canAccess={!canAccess} />
      </FormSectionCard>
      {/* 0.1.7 */}
      <FormSectionCard title={t('overview.company.industry')} disabled={!canAccess} collapsible>
        <OperationIndustry
          sectorId="overview.companyDescription.sector"
          subSectorId="overview.companyDescription.subsector"
          industryGroupId="overview.companyDescription.industryGroup"
          industryId="overview.companyDescription.industry"
          disabled={!canAccess}
        />
      </FormSectionCard>

      <FormSectionCard>
        <Grid container gap={3}>
          <Grid container spacing={3}>
            <Grid item xs={6.25}>
              {/* 0.1.8 */}
              <NumberInputControlled
                control={control}
                disabled={!canAccess}
                id="overview.company.numberOfEmployers"
                name={concatWithDot(globalPrefix, GLOBAL_FIELD_NAMES.NUMBER_OF_EMPLOYEES)}
                label={t('overview.company.numberOfEmployers')}
              />
            </Grid>
          </Grid>
          <Grid container spacing={3}>
            <Grid item xs={6.25}>
              {/* 0.1.9 */}
              <CurrencyAutocomplete label={t('selectCurrencyForEntireDocument')} disabled={!canAccess} />
            </Grid>
            <Grid item xs={5.75}>
              {/* 0.1.10 */}
              <NumberInputControlled
                control={control}
                disabled={!canAccess}
                adornmentType="currency"
                id="overview.company.averageAnnualIncome"
                name={concatWithDot(globalPrefix, GLOBAL_FIELD_NAMES.ANNUAL_INCOME)}
                label={t('overview.company.averageAnnualIncome')}
              />
            </Grid>
          </Grid>
        </Grid>
      </FormSectionCard>
    </Box>
  );
};
